import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import api, { apiError, inr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MapPin, Clock, ShieldCheck, Wallet, KeyRound, RefreshCw, CheckCircle2, MessageCircle } from "lucide-react";

export default function BuyDrawer({ listing, open, onOpenChange }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("confirm"); // "confirm" | "otp"
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setStep("confirm"); setTxn(null); setLoading(false); }
  }, [open]);

  if (!listing) return null;
  const pp = listing.pickup_point;

  const confirmBuy = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/buy`);
      setTxn(data);
      setStep("otp");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Could not start the purchase"));
    } finally { setLoading(false); }
  };

  const regenerate = async () => {
    if (!txn) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/transactions/${txn.id}/regenerate-otp`);
      setTxn((t) => ({ ...t, otp: data.otp, otp_expires_at: data.otp_expires_at }));
      toast.success("A fresh code was generated.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Could not regenerate the code"));
    } finally { setLoading(false); }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-w-lg mx-auto" data-testid="buy-drawer">
        {step === "confirm" ? (
          <>
            <DrawerHeader>
              <DrawerTitle className="font-display text-xl">Confirm your purchase</DrawerTitle>
              <DrawerDescription>Review the handoff details before you confirm.</DrawerDescription>
            </DrawerHeader>

            <div className="px-4 space-y-4">
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                <img src={listing.images?.[0]} alt={listing.title} className="h-16 w-16 rounded-lg object-cover bg-slate-100" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink truncate">{listing.title}</div>
                  <div className="text-xs text-slate-500 truncate">Seller · {listing.seller?.name}</div>
                </div>
                <div className="font-display font-extrabold text-xl text-ink">{inr(listing.price)}</div>
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <MapPin size={16} className="text-crimson mt-0.5 shrink-0" />
                  <div>
                    <div className="text-xs label-mono text-slate-400">Pickup point</div>
                    <div className="font-medium text-ink text-sm">{pp?.name || "Set by seller in chat"}</div>
                    {pp && <div className="text-xs text-slate-500">{pp.zone} · {pp.landmark}</div>}
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Clock size={16} className="text-brand mt-0.5 shrink-0" />
                  <div>
                    <div className="text-xs label-mono text-slate-400">Handoff window</div>
                    <div className="font-medium text-ink text-sm">{pp?.hours || "Coordinate a time via chat"}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2.5 text-xs text-slate-600 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <Wallet size={15} className="text-amber-600 mt-0.5 shrink-0" />
                <span>
                  You pay the seller <strong>directly</strong> (cash or their UPI) in person at handoff.
                  ReKTR never handles item payments — we only confirm the handoff happened.
                </span>
              </div>
            </div>

            <DrawerFooter>
              <Button onClick={confirmBuy} disabled={loading} className="w-full h-12 bg-brand hover:bg-brand-700 text-white text-base" data-testid="confirm-buy-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <>Confirm Buy · {inr(listing.price)}</>}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full h-11 border-slate-300" data-testid="buy-cancel-btn">
                Cancel
              </Button>
            </DrawerFooter>
          </>
        ) : (
          <>
            <DrawerHeader>
              <DrawerTitle className="font-display text-xl flex items-center gap-2">
                <CheckCircle2 size={20} className="text-emerald-500" /> Purchase reserved
              </DrawerTitle>
              <DrawerDescription>Show this code to the seller at handoff.</DrawerDescription>
            </DrawerHeader>

            <div className="px-4 space-y-4">
              <div className="rounded-2xl border-2 border-dashed border-brand/40 bg-brand/5 p-5 text-center">
                <div className="flex items-center justify-center gap-1.5 label-mono text-brand text-xs mb-2">
                  <KeyRound size={13} /> Your proof-of-purchase code
                </div>
                <div className="font-mono font-extrabold text-4xl tracking-[0.35em] text-ink pl-[0.35em]" data-testid="buy-otp-code">
                  {txn?.otp}
                </div>
                {txn?.otp_expires_at && (
                  <div className="mt-2 text-xs text-slate-500">Valid for 24 hours</div>
                )}
              </div>

              <div className="flex items-start gap-2.5 text-xs text-slate-600 rounded-xl bg-slate-50 border border-slate-200 p-3">
                <ShieldCheck size={15} className="text-emerald-500 mt-0.5 shrink-0" />
                <span>
                  Meet the seller at the pickup point, exchange the item and payment,
                  then read out this code. The item is marked <strong>Sold</strong> only after the
                  seller enters it — never before.
                </span>
              </div>

              <Button variant="outline" onClick={regenerate} disabled={loading} className="w-full h-10 border-slate-300" data-testid="buy-regenerate-btn">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <><RefreshCw size={15} className="mr-1.5" /> Regenerate code</>}
              </Button>
              <p className="text-center text-xs text-slate-500 flex items-center justify-center gap-1.5"><MessageCircle size={13} /> We've messaged the seller in your chat to coordinate pickup.</p>
            </div>

            <DrawerFooter>
              <Button onClick={() => { onOpenChange(false); navigate("/deals"); }} className="w-full h-12 bg-ink hover:bg-slate-800 text-white text-base" data-testid="buy-view-deals-btn">
                View in My Deals
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full h-10" data-testid="buy-done-btn">
                Done
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
